<template>
  <OareContentView
    :title="`Add New Text to ${collectionName}`"
    :loading="loading"
  >
    <v-alert
      v-if="draftRestored"
      type="info"
      variant="tonal"
      class="mb-4 test-draft-notice"
    >
      Restored the text you were adding. It stays saved in this browser until
      you submit it.
      <span v-if="propertiesNeedReselecting">
        Additional properties are not saved; select them again on Text
        Info.</span
      >
      <template #append>
        <v-btn variant="text" @click="discardDraft" class="test-discard-draft"
          >Discard draft</v-btn
        >
      </template>
    </v-alert>
    <v-alert
      v-if="draftTooLarge"
      type="warning"
      variant="tonal"
      class="mb-4 test-draft-too-large"
    >
      This text is too large to keep as a draft in this browser. It is not saved
      until you submit it.
    </v-alert>
    <v-stepper v-model="step">
      <v-stepper-header>
        <v-stepper-item :complete="step > 1" :value="1" title="Text Info" />
        <v-divider />
        <v-stepper-item :complete="step > 2" :value="2" title="Upload Images" />
        <v-divider />
        <v-stepper-item :complete="step > 3" :value="3" title="Editor" />
        <v-divider />
        <v-stepper-item :complete="step > 4" :value="4" title="Lexical Info" />
        <v-divider />
        <v-stepper-item :value="5" title="Final Preview" />
      </v-stepper-header>

      <v-stepper-window>
        <v-stepper-window-item :value="1">
          <text-info-set
            :existingTextRow="existingTextRow"
            :initialTextInfo="existingTextUuid ? undefined : draft?.textInfo"
            @update-text-info="setTextInfo"
            @step-complete="stepOneComplete = $event"
          />
          <stepper-button
            :showBackButton="false"
            :blockContinue="!stepOneComplete"
            blockContinueText="To continue, please include a text name. Please also include a prefix and number for at least one of the following: Excavation, Museum, Publication"
            @next="next"
          />
        </v-stepper-window-item>

        <v-stepper-window-item :value="2">
          <add-photos
            v-if="step >= 2"
            @update-photos="setPhotos"
            @step-complete="stepTwoComplete = $event"
            @skip="next"
          />
          <stepper-button
            :blockContinue="!stepTwoComplete"
            blockContinueText='To continue, please select "Confirm" or "Skip" in the awknowledgement box. Each image must also have a side and view selected before you can continue.'
            @next="next"
            @previous="previous"
          />
        </v-stepper-window-item>

        <v-stepper-window-item :value="3">
          <add-text-editor
            :initialSides="draft?.sides"
            @restored="endRestore"
            @update-editor-content="setEditorContent"
            @step-complete="stepThreeComplete = $event"
          />
          <stepper-button
            :blockContinue="!stepThreeComplete"
            blockContinueText="To continue, there must be at least one side. No empty sides,
            columns, or rows are allowed. All signs and markup must be valid."
            :continueAction="buildTables"
            @next="next"
            @previous="previous"
          />
        </v-stepper-window-item>

        <v-stepper-window-item :value="4">
          <connect-discourse
            v-if="step >= 4"
            :epigraphicUnits="epigraphyDetails.units"
            :discourseRows="
              temporaryLocalTables ? temporaryLocalTables.discourses : []
            "
            :manualDiscourseSelections="manuallySelectedDiscourses"
            @update-discourse-rows="updateDiscourseRows($event)"
            @update-manual-selections="updateManualSelections($event)"
            @step-complete="stepFourComplete = $event"
          />
          <stepper-button
            @next="next"
            @previous="previous"
            :blockContinue="!stepFourComplete"
          />
        </v-stepper-window-item>

        <v-stepper-window-item :value="5">
          <final-preview
            v-if="step >= 5"
            :epigraphyDetails="epigraphyDetails"
            :photoUrls="epigraphyLabelLinks"
            :localDiscourseInfo="
              temporaryLocalTables ? temporaryLocalTables.discourses : []
            "
          />
          <v-alert
            v-if="createStatus"
            type="info"
            variant="tonal"
            class="ma-4 test-create-status"
            >{{ createStatus }}</v-alert
          >
          <stepper-button
            continueButtonText="Submit"
            continueErrorText="Error creating text. Your work is kept; press Retry to try again."
            :continueAction="createText"
            @previous="previous"
            @next="pushToText"
          />
        </v-stepper-window-item>
      </v-stepper-window>
    </v-stepper>
  </OareContentView>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  onMounted,
  onBeforeMount,
  onBeforeUnmount,
  computed,
  ComputedRef,
} from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import sl from '@/serviceLocator';
import { confirmNavigation } from '@/utils/confirmNavigation';
import { isResponseLostError } from '@/utils/ResponseLostError';
import { v4 } from 'uuid';
import AddTextEditor from './Editor/AddTextEditor.vue';
import TextInfoSet from './TextInfo/TextInfoSet.vue';
import AddPhotos from './Photos/AddPhotos.vue';
import ConnectDiscourse from './Discourse/ConnectDiscourse.vue';
import FinalPreview from '@/views/Texts/CollectionTexts/AddTexts/Preview/FinalPreview.vue';
import StepperButton, { StepError } from './components/StepperButton.vue';
import {
  AddTextEditorContent,
  AddTextInfo,
  TextPhoto,
  EpigraphyResponse,
  TextDiscourseRow,
  TextPhotoWithDetails,
  TextRow,
  EpigraphyLabelLink,
} from '@oare/types';
import {
  convertTablesToDiscourseUnits,
  convertTablesToUnits,
} from './utils/convertTablesToUnits';
import { AddTextTables, createNewTextTables } from './utils/buildTables';
import {
  addDetailsToTextPhotos,
  completePhotos,
  uploadPendingTextPhotos,
} from './utils/photos';
import { pollUntil } from './utils/pollUntil';
import { ManualSpellingSelections } from './utils/spellingSelection';
import {
  clearDraft,
  createDraftSaver,
  DraftSide,
  draftSidesFromContent,
  draftTextInfo,
  hasChosenProperties,
  loadDraft,
  saveDraft,
} from './utils/draft';

export default defineComponent({
  props: {
    collectionUuid: {
      type: String,
      required: true,
    },
    existingTextUuid: {
      type: String,
      required: false,
    },
  },
  components: {
    AddTextEditor,
    TextInfoSet,
    AddPhotos,
    ConnectDiscourse,
    FinalPreview,
    StepperButton,
  },
  setup(props) {
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');
    const store = sl.get('store');
    const router = sl.get('router');

    const collectionName = ref('');
    const step = ref(1);
    const loading = ref(false);

    const stepOneComplete = ref(false);
    const stepTwoComplete = ref(false);
    const stepThreeComplete = ref(false);
    const stepFourComplete = ref(false);

    const next = () => (step.value += 1);
    const previous = () => (step.value -= 1);

    const isDirty = ref(true);

    onBeforeRouteLeave(() => {
      flushDraft();
      return confirmNavigation(
        isDirty.value,
        actions.showUnsavedChangesWarning
      );
    });

    const existingTextRow = ref<TextRow>();

    onMounted(async () => {
      loading.value = true;
      try {
        collectionName.value = (
          await server.getCollectionInfo(props.collectionUuid)
        ).name;

        if (props.existingTextUuid) {
          const alreadyHasEpigraphy = await server.hasEpigraphy(
            props.existingTextUuid
          );
          if (alreadyHasEpigraphy) {
            isDirty.value = false;
            router.replace({ name: '403' });
          }

          existingTextRow.value =
            (await server.getTextRowByUuid(props.existingTextUuid)) ||
            undefined;
        }
      } catch (err) {
        isDirty.value = false;
        actions.showErrorSnackbar(
          'Error loading collection name. Please try again.',
          err as Error
        );
      } finally {
        loading.value = false;
      }
    });

    // The typed text and text info are saved in this browser on every change
    // and restored on the next visit, until the text is created.
    const draftOwner = {
      userUuid: store.getters.user?.uuid,
      collectionUuid: props.collectionUuid,
      textUuid: props.existingTextUuid,
    };
    const draft = loadDraft(draftOwner);
    const draftSides = ref<DraftSide[]>(draft?.sides || []);
    // The uuid a new text gets, fixed for this import so Back, Continue,
    // Retry and a reload all submit the same text.
    const newTextUuid = draft?.newTextUuid || v4();
    // Any saved work counts: typed rows or, for a new text, a filled-in Text
    // Info field. An existing text supplies its own Text Info.
    const draftRestored = ref(
      !!draft &&
        (draft.sides.length > 0 ||
          (!props.existingTextUuid &&
            Object.values(draft.textInfo || {}).some(value => !!value)))
    );
    // The restored page starts without the chosen properties (they cannot be
    // restored), so keep the restored flag until the user picks some again.
    const propertiesReselected = ref(false);
    const propertiesNeedReselecting = computed(
      () => !!draft?.hadProperties && !propertiesReselected.value
    );
    const writeDraft = () => {
      // An existing text's identifiers come from the text, so its draft holds
      // only typed rows, and there is nothing to keep before the first row.
      if (props.existingTextUuid) {
        if (draftSides.value.length === 0) {
          clearDraft(draftOwner);
          return;
        }
      }
      const saved = saveDraft(draftOwner, {
        textInfo: props.existingTextUuid
          ? undefined
          : textInfo.value
            ? draftTextInfo(textInfo.value)
            : draft?.textInfo,
        hadProperties:
          propertiesReselected.value && textInfo.value
            ? hasChosenProperties(textInfo.value.properties)
            : draft?.hadProperties,
        sides: draftSides.value,
        newTextUuid,
      });
      // The browser cannot keep it: drop the now stale stored draft, stop
      // saving for this visit, and say so once if the text is too large.
      if (saved !== 'saved') {
        draftTooLarge.value = saved === 'too-large';
        draftSaver.close();
      }
    };
    // Saves are debounced: a restore or fast typing reports the whole text
    // many times a second. flushDraft writes a pending save at once; it runs
    // before Submit, on leaving the page and on unload. Discard and a
    // successful create close the draft for good.
    const draftTooLarge = ref(false);
    const draftSaver = createDraftSaver(writeDraft, () =>
      clearDraft(draftOwner)
    );
    const saveCurrentDraft = draftSaver.schedule;
    const flushDraft = draftSaver.flush;
    // Starts the import over from an empty page.
    const discardDraft = () => {
      draftSaver.close();
      isDirty.value = false;
      window.location.reload();
    };

    const textInfo = ref<AddTextInfo>();
    const setTextInfo = (updatedTextInfo: AddTextInfo) => {
      textInfo.value = updatedTextInfo;
      if (hasChosenProperties(updatedTextInfo.properties)) {
        propertiesReselected.value = true;
      }
      saveCurrentDraft();
    };

    const photos = ref<TextPhoto[]>([]);
    const setPhotos = (updatedPhotos: TextPhoto[]) => {
      photos.value = updatedPhotos;
    };
    const photosWithName = ref<TextPhotoWithDetails[]>([]);
    const photoUrls = computed(() =>
      photosWithName.value.filter(photo => photo.url).map(photo => photo.url)
    );
    const epigraphyLabelLinks: ComputedRef<EpigraphyLabelLink[]> = computed(
      () =>
        photos.value.map(photo => ({
          link: photo.url || '',
          label: `${store.getters.user?.firstName} ${store.getters.user?.lastName}`,
          side: photo.side || null,
          view: photo.view || null,
        }))
    );

    const editorContent = ref<AddTextEditorContent>();
    // While a draft is being restored, the editor can report sides before
    // their rows, so the stored draft is kept until the editor says every
    // restored row has reported (`restored`).
    let restoringSides = draftSides.value.length > 0;
    const saveEditorSides = () => {
      if (editorContent.value) {
        draftSides.value = draftSidesFromContent(editorContent.value);
        saveCurrentDraft();
      }
    };
    const endRestore = () => {
      if (restoringSides) {
        restoringSides = false;
        saveEditorSides();
      }
    };
    const setEditorContent = (updatedEditorContent: AddTextEditorContent) => {
      editorContent.value = updatedEditorContent;
      if (!restoringSides) {
        saveEditorSides();
      }
    };

    const temporaryLocalTables = ref<AddTextTables>();
    const buildTables = async () => {
      if (textInfo.value && editorContent.value) {
        photosWithName.value = await addDetailsToTextPhotos(
          textInfo.value.excavationPrefix,
          textInfo.value.excavationNumber,
          textInfo.value.museumPrefix,
          textInfo.value.museumNumber,
          textInfo.value.publicationPrefix,
          textInfo.value.publicationNumber,
          completePhotos(photos.value),
          uploadedPhotos
        );

        temporaryLocalTables.value = await createNewTextTables(
          textInfo.value,
          editorContent.value,
          manualSpellings.value,
          photosWithName.value,
          props.collectionUuid,
          existingTextRow.value?.uuid || newTextUuid
        );
      }
    };

    const epigraphyDetails: ComputedRef<EpigraphyResponse> = computed(() => {
      return {
        canWrite: false,
        text: {
          uuid: temporaryLocalTables.value
            ? temporaryLocalTables.value.text.uuid
            : '',
          type: 'logosyllabic',
          name:
            textInfo.value && textInfo.value.textName
              ? textInfo.value.textName
              : '',
          excavationPrefix:
            textInfo.value && textInfo.value.excavationPrefix
              ? textInfo.value.excavationPrefix
              : '',
          excavationNumber:
            textInfo.value && textInfo.value.excavationNumber
              ? textInfo.value.excavationNumber
              : '',
          museumPrefix:
            textInfo.value && textInfo.value.museumPrefix
              ? textInfo.value.museumPrefix
              : '',
          museumNumber:
            textInfo.value && textInfo.value.museumNumber
              ? textInfo.value.museumNumber
              : '',
          publicationPrefix:
            textInfo.value && textInfo.value.publicationPrefix
              ? textInfo.value.publicationPrefix
              : '',
          publicationNumber:
            textInfo.value && textInfo.value.publicationNumber
              ? textInfo.value.publicationNumber
              : '',
        },
        collection: {
          uuid: props.collectionUuid,
          name: collectionName.value,
        },
        cdliNum:
          textInfo.value && textInfo.value.cdliNum
            ? textInfo.value.cdliNum
            : '',
        units: temporaryLocalTables.value
          ? convertTablesToUnits(temporaryLocalTables.value)
          : [],
        color: '',
        colorMeaning: '',
        discourseUnits: temporaryLocalTables.value
          ? convertTablesToDiscourseUnits(
              temporaryLocalTables.value.epigraphies,
              temporaryLocalTables.value.discourses
            )
          : [],
        hasEpigraphy: true,
        zoteroData: [],
      };
    });

    // Only the user's own choices are kept, a cleared selection included, so
    // going Back and Continue again re-applies them instead of the auto pick.
    const manualSpellings = ref<ManualSpellingSelections>({});
    const updateDiscourseRows = (discourses: TextDiscourseRow[]) => {
      if (temporaryLocalTables.value) {
        temporaryLocalTables.value = {
          ...temporaryLocalTables.value,
          discourses,
        };
      }
    };

    const manuallySelectedDiscourses = computed(() =>
      Object.keys(manualSpellings.value)
    );
    const updateManualSelections = (discourseUuid: string) => {
      const row = temporaryLocalTables.value?.discourses.find(
        discourse => discourse.uuid === discourseUuid
      );
      manualSpellings.value[discourseUuid] = {
        spellingUuid: row?.spellingUuid || null,
        transcription: row?.transcription || null,
      };
    };

    // Photos upload before the create, so a failed upload leaves nothing in
    // the database and Retry is always safe. A Retry skips a photo whose
    // exact file already went up under its name. The upload route never
    // overwrites an object, so a file replaced for the same side and view
    // gets the next designator when the names are built (correctPhotoNames),
    // and a name an earlier object still holds is skipped at upload
    // (uploadUnderFreeName).
    const uploadedPhotos = new Map<string, File | undefined>();
    // Set once this visit has sent a create, so a text found under the
    // import's uuid afterwards is known to be this visit's.
    let createSentThisVisit = false;
    const finishCreate = () => {
      if (pageLeft) {
        throw new Error('The page was left before the create finished.');
      }
      isDirty.value = false;
      draftSaver.close();
    };
    const uploadPendingPhotos = () =>
      uploadPendingTextPhotos(
        photosWithName.value,
        temporaryLocalTables.value!.resources,
        uploadedPhotos,
        photo => server.uploadImage(photo, { skipErrorPage: true })
      );

    // After a lost response the server may still be committing (the gateway
    // cuts requests at 60 s, the transaction runs on), so the page checks
    // for the text every few seconds for a while before offering Retry.
    const createStatus = ref('');
    const PROBE_INTERVAL_MS = 5000;
    const PROBE_ATTEMPTS = 36; // 3 minutes
    const textExists = (uuid: string) =>
      server
        .getTextRowByUuid(uuid)
        .then(row => !!row)
        .catch(() => false);
    // Set when the page goes away: a poll still running stops, and a create
    // that finishes afterwards never navigates the user.
    let pageLeft = false;
    const waitForText = async (uuid: string): Promise<boolean> => {
      createStatus.value =
        'The server did not answer; checking whether the text was saved...';
      try {
        return await pollUntil(() => textExists(uuid), {
          intervalMs: PROBE_INTERVAL_MS,
          attempts: PROBE_ATTEMPTS,
          isCancelled: () => pageLeft,
        });
      } finally {
        createStatus.value = '';
      }
    };

    const createText = async () => {
      flushDraft();
      if (!temporaryLocalTables.value) {
        throw new Error('The text tables were not built.');
      }
      const { signInfo: _signInfo, ...tables } = temporaryLocalTables.value;
      // A new text keeps one uuid for the whole import (draft included), and
      // nothing else uses it, so if it exists now an earlier Submit created
      // it. After a lost response in this visit that is this import, so it
      // is done. A text left by an earlier visit may predate edits made since,
      // so the user checks it and keeps the draft.
      if (!existingTextRow.value && (await textExists(newTextUuid))) {
        if (createSentThisVisit) {
          // The saved text's resource rows name only the photos uploaded
          // before that create, so photos changed since would be orphaned.
          // Leave them out; the page opens the text, where they can be added.
          const changedPhotos = photosWithName.value.some(
            photo =>
              !uploadedPhotos.has(photo.name) ||
              uploadedPhotos.get(photo.name) !== photo.upload
          );
          if (changedPhotos) {
            actions.showSnackbar(
              'The text was already saved; photos changed since then were not added. Open the text to add them.'
            );
          }
          finishCreate();
          return;
        }
        const earlier: StepError = new Error(
          'A text from an earlier attempt of this import already exists. Check it, then Discard this draft or keep editing:'
        );
        earlier.link = {
          href: `/epigraphies/${newTextUuid}`,
          text: 'open it in a new tab',
        };
        throw earlier;
      }
      await uploadPendingPhotos();
      try {
        createSentThisVisit = true;
        await server.createText(tables);
      } catch (err) {
        // Only a create whose outcome is unknown (no response, or a gateway
        // error) may have committed; any 4xx is a real failure, even "already
        // has a transliteration", which can come from someone else's import.
        if (!isResponseLostError(err)) {
          throw err;
        }
        // An existing text with an epigraphy now could hold this import or
        // someone else's, so the page cannot tell; the user checks the text
        // and keeps the draft until then.
        if (existingTextRow.value) {
          const unknown: StepError = new Error(
            'The server did not answer. Check whether your transliteration was saved before retrying:'
          );
          unknown.link = {
            href: `/epigraphies/${tables.text.uuid}`,
            text: `open ${existingTextRow.value.name || 'the text'} in a new tab`,
          };
          throw unknown;
        }
        const appeared = await waitForText(tables.text.uuid);
        if (pageLeft) {
          throw new Error('The page was left while checking for the text.');
        }
        if (!appeared) {
          throw new Error(
            'The server did not answer and the text has not appeared after 3 minutes. Retry checks again before sending it.'
          );
        }
      }
      finishCreate();
    };

    const pushToText = () => {
      if (temporaryLocalTables.value) {
        router.push(`/epigraphies/${temporaryLocalTables.value.text.uuid}`);
      }
    };

    const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      flushDraft();
      if (isDirty.value) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    onBeforeMount(() => {
      window.addEventListener('beforeunload', beforeUnloadHandler);
    });

    onBeforeUnmount(() => {
      pageLeft = true;
      flushDraft();
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    });

    return {
      draftTooLarge,
      endRestore,
      createStatus,
      draft,
      draftRestored,
      propertiesNeedReselecting,
      discardDraft,
      actions,
      collectionName,
      step,
      loading,
      setTextInfo,
      setEditorContent,
      setPhotos,
      photoUrls,
      epigraphyDetails,
      next,
      previous,
      temporaryLocalTables,
      updateDiscourseRows,
      stepOneComplete,
      stepTwoComplete,
      stepThreeComplete,
      buildTables,
      pushToText,
      createText,
      manuallySelectedDiscourses,
      updateManualSelections,
      isDirty,
      existingTextRow,
      epigraphyLabelLinks,
      stepFourComplete,
    };
  },
});
</script>
